import React, { useState, useEffect, useCallback } from "react";

import { IEventSummary, IGateSteward, IQuery, IUser } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { useDebounce } from "src/hooks/useDebounce";
import { toast } from "src/components/Toast/ToastManager";
import LoadingSpinner from "src/components/LoadingSpinner";
import Button from "src/components/Button/Button";
import Input from "src/components/Input/Input";
import { Modal } from "src/components/Modal/Modal";

import styles from "./GateStewardsTab.module.scss";

interface IProps {
  event: IEventSummary;
  currentTab: number;
  tabIndex: number;
}

const GateStewardsTab: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [gateStewards, setGateStewards] = useState<IGateSteward[]>([]);

  // Add modal state
  const [isAddShown, setIsAddShown] = useState<boolean>(false);
  const [emailSearch, setEmailSearch] = useState<string>("");
  const [userList, setUserList] = useState<IUser[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [adding, setAdding] = useState<boolean>(false);

  const dEmailSearch = useDebounce<string>(emailSearch, 600);

  const getGateStewards = useCallback((eventId: string) => {
    apiService
      .getGateStewards(eventId)
      .then((response) => setGateStewards(response || []))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      getGateStewards(props.event._id);
    }
  }, [props.currentTab, props.tabIndex, props.event._id, getGateStewards]);

  const existingIds = new Set(gateStewards.map((gs) => gs.user_id?.toString()));

  const searchUsers = useCallback(async (term: string) => {
    if (!term.trim()) {
      setUserList([]);
      return;
    }

    setSearchLoading(true);

    const query: IQuery = {
      $limit: 5,
      sort: [{ attribute_name: "email", sort: "asc" }],
      query: {
        $and: [
          {
            attribute_name: "email",
            attribute_value: { operator: "$regex", value: term },
          },
          {
            attribute_name: "user_type",
            attribute_value: { operator: "$eq", value: "owner" },
          },
        ],
      },
    };

    try {
      const response = await apiService.getUsers(query);
      setUserList(response);
    } catch (e) {
      setUserList([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dEmailSearch && dEmailSearch.length > 0) {
      searchUsers(dEmailSearch);
    } else {
      setUserList([]);
    }
  }, [dEmailSearch, searchUsers]);

  const openAdd = () => {
    setEmailSearch("");
    setUserList([]);
    setIsAddShown(true);
  };

  const closeAdd = () => {
    setIsAddShown(false);
    setEmailSearch("");
    setUserList([]);
  };

  const handleAdd = async (selected: IUser) => {
    setAdding(true);
    try {
      const result = await apiService.addGateSteward(
        props.event._id,
        selected._id.toString(),
        {
          first_name: selected.first_name,
          last_name: selected.last_name,
          email: selected.email,
        }
      );
      setGateStewards(result || []);
      toast.show({
        title: "Gate Stewards",
        content: "Gate Steward added (also granted Volunteer capacity)",
        type: "success",
        duration: 3000,
      });
      closeAdd();
    } catch (e: any) {
      toast.show({
        title: "Gate Stewards",
        content:
          e?.response?.data?.message || "Failed to add gate steward",
        type: "fail",
        duration: 4000,
      });
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (gs: IGateSteward) => {
    try {
      const result = await apiService.removeGateSteward(
        props.event._id,
        gs.user_id.toString()
      );
      setGateStewards(result || []);
      toast.show({
        title: "Gate Stewards",
        content: "Gate Steward removed",
        type: "success",
        duration: 3000,
      });
    } catch (e: any) {
      toast.show({
        title: "Gate Stewards",
        content:
          e?.response?.data?.message || "Failed to remove gate steward",
        type: "fail",
        duration: 4000,
      });
    }
  };

  if (!loaded) {
    return (
      <div className={styles.GateStewardsTab}>
        <LoadingSpinner />
      </div>
    );
  }

  const addModalContent = (
    <div className={styles.addModal}>
      <div className={styles.modalHeader}>
        <h2>Add Gate Steward</h2>
        <p>
          Search for an existing user by email. Adding a gate steward also
          grants them the club Volunteer capacity.
        </p>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="gsEmailSearch">Search by Email Address</label>
        <Input
          id="gsEmailSearch"
          type="email"
          value={emailSearch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmailSearch(e.target.value)
          }
          placeholder="Type email to search for existing users..."
          autoComplete="off"
        />
      </div>

      {searchLoading && <div className={styles.searchLoading}>Searching…</div>}

      {!searchLoading && emailSearch.length > 0 && userList.length === 0 && (
        <div className={styles.noUserFound}>
          No users found matching "{emailSearch}"
        </div>
      )}

      {!searchLoading && userList.length > 0 && (
        <ul className={styles.usersFound}>
          {userList.map((u: IUser, index: number) => {
            const isExisting = existingIds.has(u._id.toString());
            return (
              <li
                key={index}
                className={isExisting ? styles.disabled : ""}
                onClick={() => !isExisting && !adding && handleAdd(u)}
              >
                <div className={styles.userName}>
                  {u.first_name || ""} {u.last_name || ""}
                  {isExisting && (
                    <span className={styles.existingBadge}>
                      Already a gate steward
                    </span>
                  )}
                </div>
                <div className={styles.userEmail}>{u.email || ""}</div>
              </li>
            );
          })}
        </ul>
      )}

      <div className={styles.modalActions}>
        <Button type="button" variant="secondary" onClick={closeAdd}>
          Close
        </Button>
      </div>
    </div>
  );

  return (
    <div className={styles.GateStewardsTab}>
      <div className={styles.toolbar}>
        <Button type="button" variant="default" onClick={openAdd}>
          Add Gate Steward
        </Button>
      </div>

      {gateStewards.length === 0 ? (
          <div className={styles.empty}>
            No gate stewards assigned to this event yet.
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {gateStewards.map((gs) => (
                <tr key={gs.user_id}>
                  <td>
                    {gs.first_name} {gs.last_name}
                  </td>
                  <td>{gs.email}</td>
                  <td className={styles.actionsCell}>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => handleRemove(gs)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      <Modal
        isShown={isAddShown}
        hide={closeAdd}
        modalContent={addModalContent}
        className="addGateStewardModal"
      />
    </div>
  );
};

export default GateStewardsTab;
